import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@fly/core';
import { useFeaturesConfiguration } from '@fly/shared-hooks';

import { useGetRepositoryReleaseQuery } from '../../../../store/registry.service';
import { DistributionForm } from './DistributionForm';
import {
  type DistributionFormRow,
  type OpenReleaseDistributionDialogParams,
} from './distributionFormKeys';

function buildReleaseDistributionRows({
  releaseArtifacts,
  distributionPackages,
}: {
  releaseArtifacts: Array<{
    name: string;
    packageType: string;
    versions: Array<{
      name: string;
      distribution?: { distributionUrl?: string | null } | null;
      url?: string | null;
    }>;
  }>;
  distributionPackages: string[];
}): DistributionFormRow[] {
  const artifactVersions: DistributionFormRow[] = [];

  releaseArtifacts.forEach((artifact) => {
    const isSupported = distributionPackages.includes(artifact.packageType);

    artifact.versions.forEach((version) => {
      artifactVersions.push({
        artifactName: artifact.name,
        versionName: version.name,
        packageType: artifact.packageType,
        distributed: isSupported ? version.distribution != null : false,
        isSupported,
        distributionUrl: isSupported
          ? (version.url ?? version.distribution?.distributionUrl ?? null)
          : null,
      });
    });
  });

  return artifactVersions;
}

export interface ReleaseDistributionDialogContentProps extends OpenReleaseDistributionDialogParams {
  reopenDistributionDialog?: () => void;
}

export function ReleaseDistributionDialogContent({
  releaseId,
  repoName,
  repoOwner,
  workflowName,
  workflowRunNumber,
  reopenDistributionDialog,
}: ReleaseDistributionDialogContentProps) {
  const { t } = useTranslation();
  const { distributionPackages } = useFeaturesConfiguration();
  const { data, isLoading } = useGetRepositoryReleaseQuery({
    owner: repoOwner,
    name: repoName,
    workflowName,
    workflowRunNumber,
  });

  const artifactVersions = useMemo(
    () =>
      buildReleaseDistributionRows({
        releaseArtifacts: data?.[0]?.releases?.[0]?.uploadedArtifacts ?? [],
        distributionPackages,
      }),
    [data, distributionPackages]
  );

  if (isLoading) {
    return (
      <div className='flex min-h-[160px] items-center justify-center px-fly-4'>
        <Text size='2' color='neutral' className='font-semibold'>
          {t('app.dashboard.artifacts.distribution_form.loading', {
            defaultValue: 'Loading distribution options...',
          })}
        </Text>
      </div>
    );
  }

  return (
    <DistributionForm
      releaseId={releaseId}
      releaseName={data?.[0]?.releases?.[0]?.title ?? undefined}
      artifactVersions={artifactVersions}
      reopenDistributionDialog={reopenDistributionDialog}
    />
  );
}

export { buildReleaseDistributionRows };
